import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CardBody,
  Row,
  Col,
  Card,
  Container,
  CardHeader,
  Input,
  Badge,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import BreadCrumb from '../../../../Components/Common/BreadCrumb';
import * as store from './hpsStore';
import {
  MAIN_STATUS_BADGE,
  HPS_STATUS_BADGE,
  HPS_STATUS_CARDS,
  resolveDetailRoute,
} from './hpsConstants';

// Crude HPS List — task 6 (hps-list.md). Read-only register spanning the whole
// procurement pipeline (Planning -> Procurement -> Tender Opening -> HPS);
// "View Detail" opens whichever page owns the record's current stage.
// Runs on dummy data — no backend contract exists for this screen yet.

// Starting pixel widths — user-draggable from here on (see `colWidths` state
// below). react-data-table-component has no built-in resize feature, so each
// column's `width` is driven by state instead of a fixed/`grow` value, and a
// drag handle is embedded in the header's `name` node.
const MIN_COL_WIDTH = 80;
const DEFAULT_COL_WIDTHS = {
  procId: 180,
  hpsNo: 160,
  title: 220,
  procurementType: 200,
  procurementMethod: 180,
  description: 220,
  mainStatus: 150,
  hpsStatus: 190,
  action: 90,
};

const HpsListView = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [allRecords, setAllRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const [colWidths, setColWidths] = useState(DEFAULT_COL_WIDTHS);
  const resizingRef = useRef(null);

  document.title = 'HPS List';

  // Real-time column resize: mousedown on a header's drag handle starts
  // tracking, mousemove updates that column's width live, mouseup ends it.
  // Listeners are bound on `document` (not the handle) so the drag keeps
  // working even when the cursor leaves the narrow 8px strip mid-drag.
  const handleResizeMove = useCallback(e => {
    const active = resizingRef.current;
    if (!active) return;
    const delta = e.clientX - active.startX;
    setColWidths(prev => ({
      ...prev,
      [active.key]: Math.max(MIN_COL_WIDTH, active.startWidth + delta),
    }));
  }, []);

  const handleResizeEnd = useCallback(() => {
    resizingRef.current = null;
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [handleResizeMove]);

  const handleResizeStart = useCallback(
    (key, e) => {
      e.preventDefault();
      e.stopPropagation();
      resizingRef.current = {
        key,
        startX: e.clientX,
        startWidth: colWidths[key],
      };
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [colWidths, handleResizeMove, handleResizeEnd]
  );

  // Safety net: if the component unmounts mid-drag, drop the listeners.
  useEffect(
    () => () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    },
    [handleResizeMove, handleResizeEnd]
  );

  const resizableHeader = (label, key) => (
    <div
      className='hps-col-header'
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <span className='form-label text-uppercase fw-semibold fs-13 mb-0'>
        {label}
      </span>
      <span
        role='separator'
        aria-orientation='vertical'
        title='Drag to resize column'
        onMouseDown={e => handleResizeStart(key, e)}
        onClick={e => e.stopPropagation()}
        className='hps-col-resize-handle'
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: 0,
          width: '10px',
          cursor: 'col-resize',
          zIndex: 2,
        }}
      />
    </div>
  );

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, sortField, sortDirection]);

  // The store is synchronous (in-memory dummy) but keeps the same
  // loading/pagination shape as the real CRUD screens for an easy backend swap.
  const fetchData = useCallback(() => {
    setLoading(true);
    const { items: rows, totalCount } = store.list({
      search: debouncedSearch,
      page: currentPage,
      pageSize: rowsPerPage,
      sortField,
      sortDir: sortDirection,
    });
    setItems(rows);
    setTotalRows(totalCount);
    setAllRecords(store.listAll({ search: debouncedSearch }));
    setLoading(false);
  }, [debouncedSearch, currentPage, rowsPerPage, sortField, sortDirection]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const hpsStatusSummary = useMemo(() => {
    const counts = {};
    allRecords.forEach(r => {
      counts[r.hpsStatus] = (counts[r.hpsStatus] || 0) + 1;
    });
    return counts;
  }, [allRecords]);

  const handleSort = (column, direction) => {
    setSortField(column.sortKey || null);
    setSortDirection(direction);
  };

  const handleViewDetail = row => {
    const route = resolveDetailRoute(row.mainStatus, row.id);
    if (!route) {
      toast.info(`Detail page for "${row.mainStatus}" is not available yet.`);
      return;
    }
    navigate(route);
  };

  const columns = [
    {
      name: resizableHeader('Id Procurement', 'procId'),
      sortKey: 'procId',
      sortable: true,
      wrap: true,
      width: `${colWidths.procId}px`,
      selector: row => row.procId,
      cell: row => <span className='fw-semibold'>{row.procId}</span>,
    },
    {
      name: resizableHeader('HPS No', 'hpsNo'),
      sortKey: 'hpsNo',
      sortable: true,
      wrap: true,
      width: `${colWidths.hpsNo}px`,
      selector: row => row.hpsNo,
    },
    {
      name: resizableHeader('Title', 'title'),
      sortKey: 'title',
      sortable: true,
      wrap: true,
      width: `${colWidths.title}px`,
      selector: row => row.title,
    },
    {
      name: resizableHeader('Procurement Type', 'procurementType'),
      sortKey: 'procurementType',
      sortable: true,
      wrap: true,
      width: `${colWidths.procurementType}px`,
      selector: row => row.procurementType,
    },
    {
      name: resizableHeader('Procurement Method', 'procurementMethod'),
      sortKey: 'procurementMethod',
      sortable: true,
      wrap: true,
      width: `${colWidths.procurementMethod}px`,
      selector: row => row.procurementMethod,
    },
    {
      name: resizableHeader('Description', 'description'),
      sortKey: 'description',
      sortable: true,
      wrap: true,
      width: `${colWidths.description}px`,
      selector: row => row.description,
    },
    {
      name: resizableHeader('Main Status', 'mainStatus'),
      sortKey: 'mainStatus',
      sortable: true,
      center: true,
      width: `${colWidths.mainStatus}px`,
      selector: row => row.mainStatus,
      cell: row => (
        <Badge
          color={MAIN_STATUS_BADGE[row.mainStatus] || 'secondary'}
          className='px-2'
        >
          {row.mainStatus}
        </Badge>
      ),
    },
    {
      name: resizableHeader('HPS Status', 'hpsStatus'),
      sortKey: 'hpsStatus',
      sortable: true,
      center: true,
      width: `${colWidths.hpsStatus}px`,
      wrap: true,
      selector: row => row.hpsStatus,
      cell: row => (
        <Badge
          color={HPS_STATUS_BADGE[row.hpsStatus] || 'secondary'}
          className='px-2 text-wrap'
        >
          {row.hpsStatus}
        </Badge>
      ),
    },
    {
      name: resizableHeader('Action', 'action'),
      width: `${colWidths.action}px`,
      center: true,
      cell: row => (
        <button
          className='btn btn-soft-info btn-sm btn-icon'
          title='View Detail'
          onClick={() => handleViewDetail(row)}
        >
          <i className='ri-eye-line align-bottom'></i>
        </button>
      ),
    },
  ];

  const statusCards = [
    {
      label: 'Total HPS Request',
      value: allRecords.length,
      icon: 'ri-stack-line',
      color: 'primary',
    },
    ...HPS_STATUS_CARDS.map(c => ({
      label: c.status,
      value: hpsStatusSummary[c.status] || 0,
      icon: c.icon,
      color: c.color,
    })),
  ];

  return (
    <React.Fragment>
      <div className='page-content'>
        {/* Visual hint for the header drag handles; the drag behavior itself
            is wired via onMouseDown in `resizableHeader`. */}
        <style>{`
          .hps-col-resize-handle:hover {
            background: rgba(13, 110, 253, 0.25);
          }
        `}</style>
        <Container fluid>
          <BreadCrumb title='HPS List' pageTitle='CRUDE' />

          {/* ---- Summary cards: per HPS Status (DOD item 7) ----
              `row-cols-*` (rather than fixed `xl={2}`) divides the row evenly
              by however many cards there are, so they always span the full
              width proportionally instead of leaving a gap on wide screens. */}
          <Row className='g-3 mb-1 row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-xl-5'>
            {statusCards.map(c => (
              <Col className='d-flex' key={c.label}>
                <Card
                  className={`card-animate border shadow-none bg-soft-${c.color} w-100 mb-0`}
                >
                  <CardBody className='py-3 d-flex align-items-center justify-content-center'>
                    <div className='d-flex align-items-center'>
                      <div className='flex-shrink-0 me-3'>
                        <i className={`${c.icon} fs-1 text-${c.color}`}></i>
                      </div>
                      <div className='text-center'>
                        <p className='text-muted mb-1 fw-semibold text-uppercase fs-11'>
                          {c.label}
                        </p>
                        <h4 className='mb-0'>{c.value}</h4>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          <Row>
            <Col lg={12}>
              <Card id='HpsList'>
                <CardHeader className='border-0'>
                  <div className='d-flex align-items-center flex-wrap gap-2'>
                    <h5 className='card-title mb-0 flex-grow-1'>HPS List</h5>
                    <div className='flex-shrink-0'>
                      <div className='d-flex gap-2 flex-wrap'>
                        <div className='search-box'>
                          <Input
                            type='text'
                            className='form-control form-control-sm'
                            placeholder='Search Title / Id / Description...'
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardBody className='pt-0'>
                  <div className='table-responsive'>
                    <DataTable
                      columns={columns}
                      data={items}
                      highlightOnHover
                      pagination
                      paginationServer
                      paginationTotalRows={totalRows}
                      paginationPerPage={rowsPerPage}
                      paginationDefaultPage={currentPage}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      onChangePage={page => setCurrentPage(page)}
                      onChangeRowsPerPage={newPerPage => {
                        setRowsPerPage(newPerPage);
                        setCurrentPage(1);
                      }}
                      sortServer
                      onSort={handleSort}
                      wrap
                      dense
                      striped
                      progressPending={loading}
                      noDataComponent={
                        <div
                          style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6c757d',
                          }}
                        >
                          <i
                            className='ri-database-2-line'
                            style={{
                              fontSize: '32px',
                              marginBottom: '8px',
                              display: 'block',
                            }}
                          ></i>
                          No HPS request found
                        </div>
                      }
                      customStyles={{
                        headCells: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            borderRight: '1px solid #dee2e6',
                            overflow: 'visible',
                            // `alignItems: stretch` (the flex default) lets
                            // the `resizableHeader` wrapper's `height: 100%`
                            // fill the whole cell, so its drag handle spans
                            // the full header row instead of just the text's
                            // own line height. Centering is handled inside
                            // that wrapper instead of here.
                            display: 'flex',
                          },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                            borderRight: '1px solid #dee2e6',
                            // Match the header: center cell content both ways
                            // so every column's title lines up with its data.
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                          },
                        },
                        rows: { style: { minHeight: '50px' } },
                      }}
                    />
                    <ToastContainer closeButton={false} limit={1} />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default HpsListView;
